import React, { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import axios from "axios";
import { BOX_MGMT_URL } from "../../../constants/config";
import { getSettingKey, getTimeByTimeZone } from "../../modules/Common/utils";
import { dateFormatForShowOnFrontend, DELIVERY_SLOT, getNameByValue } from "../../modules/Common/constants";

const PRIMARY_COLOR = "#E91E63";
const PRIMARY_COLOR_TINT = "#FCE4EC";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function PinInput({ value, onChange, onComplete, disabled }) {
  const inputs = useRef([]);

  const handleChange = (index, e) => {
    const val = e.target.value.replace(/\D/g, "");
    if (!val) return;
    const digit = val.slice(-1);
    const newPin = value.split("");
    newPin[index] = digit;
    const pin = newPin.join("");
    onChange(pin);
    if (index < 3 && digit) {
      inputs.current[index + 1]?.focus();
    }
    if (pin.length === 4 && !pin.includes(" ") && pin.replace(/\d/g, "") === "") {
      onComplete?.(pin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newPin = value.split("");
      if (newPin[index] && newPin[index] !== " ") {
        newPin[index] = " ";
        onChange(newPin.join(""));
      } else if (index > 0) {
        newPin[index - 1] = " ";
        onChange(newPin.join(""));
        inputs.current[index - 1]?.focus();
      }
      e.preventDefault();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (!pasted) return;
    e.preventDefault();

    onChange(pasted.padEnd(4, " "));
    if (pasted.length === 4) {
      onComplete?.(pasted);
    } else {
      inputs.current[pasted.length]?.focus();
    }
  };

  return (
    <div style={{ display: "flex", gap: 12, justifyContent: "center", margin: "20px 0" }}>
      {[0, 1, 2, 3].map((i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="password"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={`PIN digit ${i + 1}`}
          maxLength={1}
          disabled={disabled}
          value={value[i] && value[i] !== " " ? value[i] : ""}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          style={{
            width: 56,
            height: 56,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 600,
            border: `2px solid ${PRIMARY_COLOR}`,
            borderRadius: 8,
            outline: "none",
            color: "#333",
            backgroundColor: disabled ? "#f4f6f9" : "#fff",
          }}
        />
      ))}
    </div>
  );
}

export default function BoxViewPage() {
  const query = useQuery();
  const saleOrderId = query.get("so");
  const boxId = query.get("box");
  const publicKey = query.get("public_key");

  const { currentState } = useSelector((state) => ({ currentState: state.basicSettings }), shallowEqual);
  const timezone = getSettingKey(currentState?.entity, "Basic", "timezone");

  const [passcode, setPasscode] = useState("    ");
  const [boxData, setBoxData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleVerify = async (pin) => {
    const code = (pin || passcode).replace(/\s/g, "");
    if (code.length !== 4) {
      setError("Please enter a 4-digit PIN.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${BOX_MGMT_URL}/box-view/verify?public_key=${publicKey}`,
        {
          sale_order_id: Number(saleOrderId),
          box_id: Number(boxId),
          passcode: code,
        }
      );
      setBoxData(data);
    } catch (err) {
      const msg =
        err.response?.data?.errors?.[0] ||
        err.response?.data?.message ||
        "Verification failed. Please try again.";
      setError(msg);
      setBoxData(null);
    } finally {
      setLoading(false);
    }
  };

  if (!saleOrderId || !boxId || !publicKey) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h2 style={styles.title}>Invalid Link</h2>
          <p style={styles.text}>This QR code link is missing required information.</p>
        </div>
      </div>
    );
  }

  if (!boxData) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, maxWidth: 400, textAlign: "center" }}>
          <div style={styles.lockIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={PRIMARY_COLOR} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 style={{ ...styles.title, textAlign: "center" }}>Confirm Box Load</h2>
          <p style={styles.text}>
            Enter your 4-digit PIN to authorize <strong>Box {boxId}</strong> for loading.
          </p>
          <PinInput
            value={passcode}
            onChange={setPasscode}
            onComplete={handleVerify}
            disabled={loading}
          />
          {error && <p style={styles.error}>{error}</p>}
          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <button
              onClick={() => setPasscode("    ")}
              disabled={loading}
              style={styles.cancelBtn}
            >
              Cancel
            </button>
            <button
              onClick={() => handleVerify()}
              disabled={loading}
              style={styles.confirmBtn}
            >
              {loading ? "Verifying..." : "Confirm"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <div style={styles.pageCard}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.boxTitle}>Box {boxData.box_number}</h1>
            <p style={styles.subtitle}>
              Total No. of Products: <strong>{String(boxData.total_products).padStart(2, "0")}</strong>
            </p>
          </div>
          <button
            onClick={() => { setBoxData(null); setPasscode("    "); }}
            style={styles.backBtn}
          >
            Back
          </button>
        </div>

        {/* Order Details */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionTitle}>Order Details</div>
          <div style={styles.infoGrid}>
            <div style={{ ...styles.infoBlock, gridColumn: "1 / -1" }}>
              <span style={styles.infoLabel}>Business Name</span>
              <span style={styles.infoValue}>{boxData.business_name || "-"}</span>
            </div>
            <div style={styles.infoBlock}>
              <span style={styles.infoLabel}>Sale Order No.</span>
              <span style={styles.infoValue}>{boxData.order_number}</span>
            </div>
            <div style={styles.infoBlock}>
              <span style={styles.infoLabel}>Customer Phone No.</span>
              <span style={styles.infoValue}>{boxData.customer_phone || "-"}</span>
            </div>
            <div style={{ ...styles.infoBlock, gridColumn: "1 / -1" }}>
              <span style={styles.infoLabel}>Address</span>
              <span style={styles.infoValue}>{boxData.address || "-"}</span>
            </div>
          </div>
        </div>

        {/* Delivery Details */}
        {boxData.delivery_date || boxData.delivery_slot != null || boxData.driver_name || boxData.vehicle_name ? (
          <div style={styles.deliveryCard}>
            <div style={styles.deliveryTitle}>Delivery Details</div>
            <div style={styles.infoGrid}>
              <div style={styles.infoBlock}>
                <span style={styles.infoLabel}>Delivery Date</span>
                <span style={styles.infoValue}>{getTimeByTimeZone(timezone, boxData.delivery_date, dateFormatForShowOnFrontend) || "-"}</span>
              </div>
              <div style={styles.infoBlock}>
                <span style={styles.infoLabel}>Delivery Slot</span>
                <span style={styles.infoValue}>{getNameByValue(boxData.delivery_slot, DELIVERY_SLOT) || "-"}</span>
              </div>
              <div style={styles.infoBlock}>
                <span style={styles.infoLabel}>Driver Name</span>
                <span style={styles.infoValue}>{boxData.driver_name || "-"}</span>
              </div>
              <div style={styles.infoBlock}>
                <span style={styles.infoLabel}>Vehicle Name</span>
                <span style={styles.infoValue}>{boxData.vehicle_name || "-"}</span>
              </div>
            </div>
          </div>
        ) : null}

        {/* Desktop Table */}
        <div className="box-view-table">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Image</th>
                <th style={{ ...styles.th, textAlign: "left" }}>Product Name</th>
                <th style={styles.th}>Packed Qty</th>
                <th style={styles.th}>SKU</th>
                <th style={styles.th}>UPC</th>
              </tr>
            </thead>
            <tbody>
              {boxData.items.map((item, i) => (
                <tr key={i}>
                  <td style={styles.td}>
                    <img
                      src={item.default_image}
                      alt={item.product_name || ""}
                      loading="lazy"
                      style={styles.productImg}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                  </td>
                  <td style={{ ...styles.td, textAlign: "left" }}>{item.product_name || "-"}</td>
                  <td style={styles.td}>{item.packed_qty}</td>
                  <td style={styles.td}>{item.sku || "-"}</td>
                  <td style={styles.td}>{item.upc || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="box-view-cards">
          {boxData.items.map((item, i) => (
            <div key={i} style={styles.mobileCard}>
              <div style={styles.mobileCardTop}>
                <div style={styles.mobileImgWrap}>
                  <img
                    src={item.default_image}
                    alt={item.product_name || ""}
                    loading="lazy"
                    style={styles.mobileImg}
                    onError={(e) => { e.target.style.opacity = 0; }}
                  />
                </div>
                <div style={styles.mobileCardInfo}>
                  <p style={styles.mobileProductName}>{item.product_name || "-"}</p>
                  <span style={styles.skuBadge}>
                    {item.sku || "-"} / {item.upc || "-"}
                  </span>
                </div>
              </div>
              <div style={styles.mobileRow}>
                <span style={styles.mobileLabel}>Packed Qty</span>
                <span style={styles.mobileQtyChip}>{item.packed_qty}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .box-view-cards { display: none; }
        @media (max-width: 768px) {
          .box-view-table { display: none !important; }
          .box-view-cards { display: block !important; }
        }
        @media (max-width: 480px) {
          .box-view-cards { margin: 0 -8px; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
    backgroundColor: "#f4f6f9",
    padding: 16,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 32,
    maxWidth: 480,
    width: "100%",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
  },
  lockIcon: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: PRIMARY_COLOR_TINT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  },
  title: {
    margin: "0 0 8px",
    fontSize: 20,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  text: {
    color: "#666",
    marginBottom: 8,
    fontSize: 14,
    lineHeight: 1.5,
  },
  error: {
    color: PRIMARY_COLOR,
    marginBottom: 8,
    fontSize: 13,
  },
  cancelBtn: {
    flex: 1,
    padding: "10px 0",
    fontSize: 15,
    backgroundColor: "#fff",
    color: "#333",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 500,
  },
  confirmBtn: {
    flex: 1,
    padding: "10px 0",
    fontSize: 15,
    backgroundColor: PRIMARY_COLOR,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 500,
  },
  pageContainer: {
    minHeight: "100vh",
    backgroundColor: "#f4f6f9",
    padding: "24px 16px",
  },
  pageCard: {
    backgroundColor: "#fff",
    borderRadius: 8,
    padding: "24px",
    maxWidth: 960,
    margin: "0 auto",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 20,
  },
  boxTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "#1a1a1a",
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 13,
    color: "#666",
  },
  backBtn: {
    padding: "6px 20px",
    fontSize: 13,
    backgroundColor: "#fff",
    color: PRIMARY_COLOR,
    border: `1px solid ${PRIMARY_COLOR}`,
    borderRadius: 4,
    cursor: "pointer",
    fontWeight: 500,
  },
  sectionCard: {
    border: "1px solid #eef0f4",
    borderRadius: 12,
    padding: "16px 18px",
    marginBottom: 16,
  },
  deliveryCard: {
    border: "1px solid rgba(233, 30, 99, 0.18)",
    backgroundColor: PRIMARY_COLOR_TINT,
    borderRadius: 12,
    padding: "16px 18px",
    marginBottom: 20,
  },
  sectionTitle: {
    margin: "0 0 14px",
    fontSize: 12,
    fontWeight: 700,
    color: "#8a92a0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  deliveryTitle: {
    margin: "0 0 14px",
    fontSize: 12,
    fontWeight: 700,
    color: PRIMARY_COLOR,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: "14px 20px",
  },
  infoBlock: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  infoLabel: {
    fontSize: 11,
    color: "#999",
    textTransform: "uppercase",
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1a1a1a",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "center",
    padding: "10px 12px",
    borderBottom: "1px solid #eee",
    backgroundColor: "#FFF3F3",
    fontSize: 12,
    fontWeight: 600,
    color: "#666",
  },
  td: {
    textAlign: "center",
    padding: "10px 12px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: 14,
    color: "#333",
  },
  productImg: {
    width: 36,
    height: 36,
    objectFit: "cover",
    borderRadius: 4,
  },
  mobileCard: {
    backgroundColor: "#fff",
    border: "1px solid #eef0f4",
    borderRadius: 14,
    padding: "14px 16px",
    marginBottom: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
  mobileCardTop: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
  },
  mobileImgWrap: {
    width: 48,
    height: 48,
    flexShrink: 0,
    borderRadius: 10,
    backgroundColor: "#f4f6f9",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  mobileImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  mobileCardInfo: {
    flex: 1,
    minWidth: 0,
  },
  mobileProductName: {
    margin: "0 0 6px",
    fontSize: 15,
    fontWeight: 600,
    color: "#1a1a1a",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  skuBadge: {
    display: "inline-block",
    backgroundColor: PRIMARY_COLOR,
    color: "#fff",
    fontSize: 12,
    fontFamily: "monospace",
    letterSpacing: "0.02em",
    padding: "3px 10px",
    borderRadius: 12,
  },
  mobileRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0 0",
    borderTop: "1px solid #f0f0f0",
  },
  mobileLabel: {
    fontSize: 13,
    color: "#999",
  },
  mobileQtyChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 26,
    padding: "2px 10px",
    borderRadius: 20,
    backgroundColor: PRIMARY_COLOR_TINT,
    color: PRIMARY_COLOR,
    fontSize: 14,
    fontWeight: 700,
  },
};